import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Plus, Pencil, Trash2, Megaphone, Repeat, Loader2 } from "lucide-react";
import { toast } from "sonner";

const RANGES = [{ v: "month", l: "This Month" }, { v: "ytd", l: "YTD" }, { v: "all", l: "All Time" }];
const blankForm = () => ({ category: "", description: "", amount: "", date: new Date().toISOString().slice(0, 10), recurring: false });

export default function Finances() {
  const [range, setRange] = useState("month");
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  const loadSummary = useCallback(() => {
    api.get(`/finances/summary?range=${range}`).then((r) => setSummary(r.data));
  }, [range]);
  const loadExpenses = () => api.get("/expenses").then((r) => setExpenses(r.data));
  const loadCats = () => api.get("/settings/expense-categories").then((r) => setCats(r.data));

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadExpenses(); loadCats(); }, []);

  const refresh = () => { loadSummary(); loadExpenses(); };

  const openAdd = () => { setEditing(null); setForm({ ...blankForm(), category: cats[0]?.name || "" }); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ category: e.category, description: e.description || "", amount: e.amount, date: (e.date || "").slice(0, 10), recurring: !!e.recurring }); setOpen(true); };

  const save = async () => {
    if (!form.category) return toast.error("Pick a category");
    if (form.amount === "" || isNaN(Number(form.amount))) return toast.error("Enter an amount");
    setSaving(true);
    const payload = { category: form.category, description: form.description, amount: Number(form.amount), date: form.date, recurring: form.recurring };
    try {
      if (editing) await api.patch(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editing ? "Expense updated" : "Expense added");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Expense deleted");
    refresh();
  };

  const catColor = (name) => cats.find((c) => c.name === name)?.color || "#94A3B8";

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="finances-page">
      <PageHeader title="Finances" subtitle="Promotion spend plus your running costs — website, AI credits, email and more.">
        <Button onClick={openAdd} data-testid="add-expense-button" className="font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Add Cost</Button>
      </PageHeader>

      <div className="flex items-center gap-1 bg-secondary/50 p-1 rounded-lg w-fit mb-6" data-testid="finances-range-toggle">
        {RANGES.map((r) => (
          <button key={r.v} onClick={() => setRange(r.v)} data-testid={`finances-range-${r.v}`}
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${range === r.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {r.l}
          </button>
        ))}
      </div>

      {!summary ? (
        <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="glass border border-border rounded-2xl p-5" data-testid="finances-total-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Spend</span>
                <Wallet className="w-4 h-4 text-primary" />
              </div>
              <p className="font-display text-3xl font-bold" data-testid="finances-grand-total">{money(summary.grand_total)}</p>
            </div>
            <div className="glass border border-border rounded-2xl p-5" data-testid="finances-promo-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Promotion Spend</span>
                <Megaphone className="w-4 h-4 text-emerald-400" />
              </div>
              <p className="font-display text-3xl font-bold" data-testid="finances-promo-total">{money(summary.promotion_spend)}</p>
              <p className="text-xs text-muted-foreground mt-2">Pulled from video promotions</p>
            </div>
            <div className="glass border border-border rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Other Costs</span>
                <DollarSignIcon />
              </div>
              <p className="font-display text-3xl font-bold">{money(summary.manual_total)}</p>
            </div>
          </div>

          <div className="glass border border-border rounded-2xl p-6 mb-6" data-testid="finances-breakdown">
            <h3 className="font-display font-semibold text-lg mb-4">Breakdown by Category</h3>
            <div className="space-y-3">
              {summary.breakdown.filter((b) => b.amount > 0).length === 0 && <p className="text-sm text-muted-foreground">No spend recorded in this range yet.</p>}
              {summary.breakdown.filter((b) => b.amount > 0).map((b) => {
                const pct = summary.grand_total ? Math.round((b.amount / summary.grand_total) * 100) : 0;
                const color = b.auto ? "#10B981" : catColor(b.category);
                return (
                  <div key={b.category} data-testid={`breakdown-row-${b.category}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2 font-medium">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                        {b.category}{b.auto && <Badge variant="secondary" className="text-[10px]">Auto</Badge>}
                      </span>
                      <span className="font-mono">{money(b.amount)} <span className="text-muted-foreground">· {pct}%</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary/60 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="glass border border-border rounded-2xl p-6" data-testid="finances-expense-list">
            <h3 className="font-display font-semibold text-lg mb-4">Your Costs</h3>
            {expenses.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No costs added yet. Click "Add Cost" to log website, AI credits, email and other expenses.</p>
            ) : (
              <div className="space-y-2">
                {expenses.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/40 border border-border" data-testid={`expense-row-${e.id}`}>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: catColor(e.category) }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{e.category}{e.description ? ` — ${e.description}` : ""}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        {e.recurring ? <span className="flex items-center gap-1 text-primary"><Repeat className="w-3 h-3" /> Monthly from {new Date(e.date).toLocaleDateString()}</span> : new Date(e.date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="font-mono font-semibold">{money(e.amount)}{e.recurring && <span className="text-muted-foreground text-xs">/mo</span>}</span>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(e)} data-testid={`expense-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(e.id)} data-testid={`expense-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-border" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Cost" : "Add Cost"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description-input" placeholder="e.g. OpenAI credits" /></div>
            <div className="space-y-2"><Label>{form.recurring ? "Start date" : "Date"}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input" /></div>
            <div className="flex items-center justify-between rounded-xl bg-secondary/40 border border-border p-3">
              <div>
                <Label className="flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5" /> Recurring monthly</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Repeats automatically every month from the start date.</p>
              </div>
              <Switch checked={form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} data-testid="expense-recurring-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="expense-save" className="font-semibold">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving...</> : editing ? "Save Changes" : "Add Cost"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DollarSignIcon() {
  return <span className="text-cyan-400 font-bold text-lg leading-none">$</span>;
}
